import { FormControl, FormHelperText } from "@mui/material"
import { observer } from "mobx-react"
import { t } from "@lingui/macro"
import { useEffect, useMemo, useCallback } from "react"

import { InfoCenterAdminService } from "src/api"

import { getItemTypeText } from "src/helpers/getItemTypeText"
import { useStore } from "src/store/lib/useStore"
import { LibraryInformationItemStore } from "src/modals/library-information-items/store"
import { reportError } from "src/lib/report"
import { ItemTypes } from "src/types/library-information-items/item-types"
import { AutoCompleteTextField } from "src/components/AutoCompleteTextField"

export const LibraryInformationItemsType = observer(() => {
    const store = useStore(LibraryInformationItemStore)

    useEffect(() => {
        ;(async () => {
            try {
                store.setItemTypes(
                    (await InfoCenterAdminService.infocenterGetInformationTypes()) as ItemTypes[],
                )
            } catch (error) {
                reportError(
                    t`edit-information-item-modal.load-types-fail`,
                    error,
                )
            }
        })()
    }, [store])

    const typeOptions = useMemo(() => {
        return store.itemTypes.map((type) => ({
            id: type,
            label: getItemTypeText(type),
        }))
    }, [store.itemTypes])

    const selectedOption = useMemo(() => {
        const currentType = store.formFields.get("type")
        if (currentType === undefined) return null

        return {
            id: currentType as ItemTypes,
            label: getItemTypeText(currentType as ItemTypes),
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [store.formFields.get("type")])

    const handleTypeChange = useCallback(
        (_, option) => {
            if (option != null) {
                store.formFields.set("type", option.id as ItemTypes)
            }
        },
        [store.formFields],
    )

    return (
        <FormControl>
            {store.itemTypes.length > 0 && (
                <AutoCompleteTextField
                    id="type"
                    options={typeOptions}
                    value={selectedOption}
                    onChange={handleTypeChange}
                    placeholder={t`edit-information-item-modal.type`}
                    label={t`edit-information-item-modal.type`}
                    error={Boolean(store.formFields.error("type"))}
                    helperText={store.formFields.error("type") ?? undefined}
                    disabled={store.formFields.get("accessType") === "READ"}
                    disableClearable
                />
            )}
            {Boolean(store.formFields.error("type")) && (
                <FormHelperText error={true}>
                    {store.formFields.error("type")}
                </FormHelperText>
            )}
        </FormControl>
    )
})
